if (!customElements.get('tree-navigation')) {
  customElements.define('tree-navigation', class TreeNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.surface = this.querySelector('[data-nav-surface]');
      this.drawer = this.querySelector('[data-nav-drawer]');
      if (!this.surface || !this.drawer) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      // Horizon scrolls this wrapper on desktop, and the document on mobile.
      this.pageWrapper = this.closest('.page-wrapper');
      this.wrapperMedia = window.matchMedia('(min-width: 990px)');
      this.hoverMedia = window.matchMedia('(min-width: 1001px) and (hover: hover) and (pointer: fine)');
      this.hoverTimers = new Map();
      this.querySelectorAll('.tc-navigation__desktop details').forEach(details => {
        details.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.queueHover(details, true);
        }, options);
        details.addEventListener('pointerleave', event => {
          if (event.pointerType === 'mouse') this.queueHover(details, false);
        }, options);
        // A deliberate click always wins over a pending hover action.
        details.querySelector(':scope > summary')?.addEventListener('pointerdown', () => this.clearHoverTimer(details), options);
      });
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateScroll(); });
      };
      this.onResize = () => {
        const wasCompact = this.classList.contains('is-compact');
        this.classList.remove('is-compact');
        this.measureExpanded();
        this.classList.toggle('is-compact', wasCompact);
        this.closeAll(false);
        this.updateScroll();
      };
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      this.pageWrapper?.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      window.addEventListener('resize', this.onResize, options);
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeAll(false);
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (!this.querySelector('details[open]')) return;
        event.preventDefault();
        this.closeAll(true);
      }, options);
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAll(false);
      }, options);
      this.addEventListener('toggle', event => this.onToggle(event), { ...options, capture: true });
      this.addEventListener('click', event => {
        if (event.target.closest('a')) this.closeAll(false);
      }, options);
      this.observer = new ResizeObserver(() => {
        if (!this.classList.contains('is-compact')) this.measureExpanded();
        this.measureVisible();
      });
      this.observer.observe(this.surface);
      this.measureExpanded();
      this.updateScroll();
    }

    getScrollPosition() {
      return this.wrapperMedia?.matches && this.pageWrapper ? this.pageWrapper.scrollTop : window.scrollY;
    }

    measureExpanded() {
      this.expandedHeight = this.surface.getBoundingClientRect().height;
      const containerTop = this.wrapperMedia?.matches && this.pageWrapper ? this.pageWrapper.getBoundingClientRect().top : 0;
      this.threshold = this.getBoundingClientRect().top - containerTop + this.getScrollPosition() + this.expandedHeight + 24;
      // Preserve document flow when only the inner surface becomes fixed.
      this.style.height = `${this.expandedHeight}px`;
      this.measureVisible();
    }

    measureVisible() {
      const rect = this.surface.getBoundingClientRect();
      const bottom = Math.max(0, Math.min(rect.bottom, window.innerHeight - 44));
      this.style.setProperty('--tc-navigation-bottom', `${bottom}px`);
      document.documentElement.style.setProperty('--header-height', `${rect.height}px`);
      document.documentElement.style.setProperty('--header-group-height', `${rect.height}px`);
      // Horizon's product templates also write these on body, overriding :root.
      document.body.style.setProperty('--header-height', `${rect.height}px`);
      document.body.style.setProperty('--header-group-height', `${rect.height}px`);
    }

    updateScroll() {
      const compact = this.classList.contains('is-compact');
      // A separate return threshold prevents one-pixel scroll oscillation.
      const shouldCompact = this.dataset.sticky === 'true' && this.getScrollPosition() > (this.threshold - (compact ? 16 : 0));
      this.dataset.stickyState = shouldCompact ? 'active' : 'inactive';
      if (compact !== shouldCompact) {
        this.closeAll(false);
        this.classList.toggle('is-compact', shouldCompact);
      }
      this.measureVisible();
    }

    onToggle(event) {
      const details = event.target;
      if (details.tagName !== 'DETAILS') return;
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
      if (!details.open) {
        this.clearHoverTimer(details);
        return;
      }
      this.measureVisible();
      const siblings = details === this.drawer
        ? this.querySelectorAll('.tc-navigation__desktop details[open]')
        : details.parentElement.parentElement.querySelectorAll(':scope > li > details[open]');
      siblings.forEach(other => { if (other !== details) other.open = false; });
    }

    closeAll(restoreFocus) {
      this.clearHoverTimers();
      const open = Array.from(this.querySelectorAll('details[open]'));
      const active = document.activeElement;
      const owner = open.find(details => details.contains(active)) || open[0];
      open.forEach(details => { details.open = false; details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false'); });
      if (restoreFocus && owner) owner.querySelector(':scope > summary')?.focus();
    }

    queueHover(details, open) {
      this.clearHoverTimer(details);
      if (!this.hoverMedia.matches || this.classList.contains('is-compact')) return;
      const timer = window.setTimeout(() => {
        this.hoverTimers.delete(details);
        if (!this.hoverMedia.matches || this.classList.contains('is-compact')) return;
        if (open) {
          if (details.matches(':hover')) details.open = true;
        } else {
          const summary = details.querySelector(':scope > summary');
          // Let a keyboard user finish navigating links even after the mouse leaves.
          if (details.contains(document.activeElement) && document.activeElement !== summary) return;
          details.open = false;
          details.querySelectorAll('details[open]').forEach(child => { child.open = false; });
        }
      }, open ? 120 : 240);
      this.hoverTimers.set(details, timer);
    }

    clearHoverTimer(details) {
      const timer = this.hoverTimers?.get(details);
      if (timer !== undefined) window.clearTimeout(timer);
      this.hoverTimers?.delete(details);
    }

    clearHoverTimers() {
      this.hoverTimers?.forEach(timer => window.clearTimeout(timer));
      this.hoverTimers?.clear();
    }

    disconnectedCallback() {
      this.clearHoverTimers();
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.controller = null;
      this.frame = null;
    }
  });
}
